import {every, isEmpty, some} from 'lodash'
import {SUPER_USER} from '@/store/static/permissions/admin'
import * as mutationTypes from './mutation-types'
import api from '@/store/api/auth'

const initialState = () => ({
    auth: {
        first_name: '',
        last_name: '',
        email_address: '',
        user_id: '',
        token: '',
        has_mfa: false,
        has_force_mfa: false,
        force_mfa_data: null,
        role_ids: []
    },
    login: false,
    mfaError: {},
    loading: false,
    nextRoute: {},
    fromRoute: {}
})

export default {
    namespaced: true,
    state: initialState(),
    getters: {
        auth: state => state.auth,
        firstName: state => state.auth.first_name,
        lastName: state => state.auth.last_name,
        emailAddress: state => state.auth.email_address,
        userId: state => state.auth.user_id,
        hasMfaError: state => !isEmpty(state.mfaError),
        mfaError: state => state.mfaError,
        nextRoute: state => state.nextRoute,
        fromRoute: state => state.fromRoute,
        isAuthed: state => Boolean(state.auth?.token),
        permissions: state => state.auth.role_ids,
        token: state => state.auth.token,
        hasPermissions: (state, getters) => (permissions = []) => {
            const userPermissions = getters.permissions
            const isSuper = userPermissions.includes(SUPER_USER)

            if (isSuper || !permissions || isEmpty(permissions)) {
                return true
            }

            return every(permissions, permission => {
                return userPermissions.includes(permission)
            })
        },
        hasSomePermissions: (state, getters) => (permissions = []) => {
            return some(permissions, permission => {
                return getters.hasPermissions([permission])
            })
        }
    },
    actions: {
        login: async ({commit, dispatch}, data) => {
            try {
                const response = await api.login(data)
                commit(mutationTypes.SET_LOGIN, true)
                commit(mutationTypes.SET_AUTH, response)
                return response
            } catch (e) {
                const errorResponse = await dispatch('errors/addErrors', [e], {root: true})
                return Promise.reject(errorResponse[0])
            }
        },
        me: async ({commit, dispatch}, data) => {
            try {
                const response = await api.me(data)
                commit(mutationTypes.SET_AUTH, response)
                return response
            } catch (e) {
                const errorResponse = await dispatch('errors/addErrors', [e], {root: true})
                return Promise.reject(errorResponse[0])
            }
        },
        logout: async ({commit, dispatch}) => {
            try {
                const response = await api.logout()
                commit(mutationTypes.RESET_AUTH)
                return response
            } catch (e) {
                const errorResponse = await dispatch('errors/addErrors', [e], {root: true})
                return Promise.reject(errorResponse[0])
            }
        },
        ping: async ({dispatch}) => {
            try {
                return await api.ping()
            } catch (e) {
                const errorResponse = await dispatch('errors/addErrors', [e], {root: true})
                return Promise.reject(errorResponse[0])
            }
        },
        resetAuth: async ({commit}) => commit(mutationTypes.RESET_AUTH),
        resetMfaError: async ({commit}) => commit(mutationTypes.RESET_MFA_ERROR),
        setAuth: async ({commit}, data) => commit(mutationTypes.SET_AUTH, data),
        setLoading: async ({commit}, data) => commit(mutationTypes.SET_LOADING, data),
        setMfaError: async ({commit}, data) => commit(mutationTypes.SET_MFA_ERROR, data),
        setNextRoute: async ({commit}, data) => commit(mutationTypes.SET_NEXT_ROUTE, data),
        setFromRoute: async ({commit}, data) => commit(mutationTypes.SET_FROM_ROUTE, data)
    },
    mutations: {
        [mutationTypes.RESET_AUTH] (state) {
            Object.assign(state, initialState())
        },
        [mutationTypes.RESET_MFA_ERROR] (state) {
            state.mfaError = initialState().mfaError
        },
        [mutationTypes.SET_AUTH] (state, data) {
            if (data) {
                const {has_force_mfa, force_mfa_data, has_mfa} = data

                if (has_force_mfa && !has_mfa) {
                    const mfaData = force_mfa_data
                    const blockAccess = mfaData.block_access

                    if (blockAccess || state.login) {
                        state.mfaError = {
                            status: 401,
                            message: force_mfa_data.message
                        }
                    }
                }
            }

            state.auth = data
        },
        [mutationTypes.SET_LOGIN] (state, data) {
            state.login = data
        },
        [mutationTypes.SET_LOADING] (state, data) {
            state.loading = data
        },
        [mutationTypes.SET_MFA_ERROR] (state, data) {
            state.mfaError = data
        },
        [mutationTypes.SET_NEXT_ROUTE] (state, data) {
            state.nextRoute = data
        },
        [mutationTypes.SET_FROM_ROUTE] (state, data) {
            state.fromRoute = data
        }
    }
}
